import axios from "axios";
import { getCookie, deleteCookie } from "../helper/cookieHelper";
import { ACCESS_TOKEN, USER_DETAILS, USER_ID, SESSION_ERROR_SHOWN, SERVERKNOWNSALT, CHECKIN_TABLE_ID } from "./constants";
import { Store } from "./configureStore";
import { setSignupPopup } from "./features/navigation/navigationActions";
import { removeauthInfo } from "./features/auth/AuthActions";
// import { showErrorToast } from "./utils/toastUtils";
import { showErrorToast, showErrorToastDesktop } from "./utils/toastUtils";
const accessToken = getCookie("ACCESS_TOKEN");
// console.log("accessToken 6", accessToken);

// Helper function to check if session error has been shown
const hasSessionErrorBeenShown = (): boolean => {
  return sessionStorage.getItem(SESSION_ERROR_SHOWN) === "true";
};

// Helper function to mark session error as shown
const markSessionErrorAsShown = (): void => {
  sessionStorage.setItem(SESSION_ERROR_SHOWN, "true");
};

// Helper function to reset session error flag (call on successful login)
export const resetSessionErrorFlag = (): void => {
  sessionStorage.removeItem(SESSION_ERROR_SHOWN);
};

// CD (contactless dine-in) sessions are guest sessions keyed off a checked-in
// table — a 401/403 from a CD-only endpoint doesn't mean the user needs to
// sign in again, so the session-expiry popup should stay suppressed.
const isCdOrder = (): boolean => {
  return Boolean(localStorage.getItem(CHECKIN_TABLE_ID));
};

// Terms & Policies must stay readable without interrupting the visitor —
// GlobalSignupHandler (Routers.tsx) already suppresses the sign-in modal on
// this route for the same reason. A background request that 401/403s while
// reading policies (e.g. AccountsPage's loyalty/delivery-address lookups
// firing for an anonymous or session-expired visitor) shouldn't surface the
// "session expired" toast either. Not marking the error as shown here means
// the toast still fires normally the first time this happens on any other page.
const isOnPoliciesPage = (): boolean =>
  window.location.pathname.toLowerCase().includes('/account/policies');
const API = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  timeout: 60000,
  headers:{
    "X-App-Version": "v2", // or "v2"
    "X-source":"CA",
    // "ngrok-skip-browser-warning": "true" needded for local connect
  }
    // headers:{'ngrok-skip-browser-warning': 'true'},
});

// ✅ Request Interceptor
API.interceptors.request.use((config) => {
  const token = getCookie("ACCESS_TOKEN"); // Token fetched at request time
  if (token && !config?.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (
    config.url === '/api/customers/order/update' ||
    config.url === '/api/customers/add-feedback/v2'
  ) {
    delete config.headers['X-source'];
  }
  return config;
});

const NEW_API = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT_REVAMP,
  timeout: 60000,
  headers:{
    // "X-App-Version": "v2", // or "v2"
    "x-compress-response" : 1
  }
  // headers:{'ngrok-skip-browser-warning': 'true'},
});

// ✅ Request Interceptor
API.interceptors.request.use((config) => {
  const token = getCookie("ACCESS_TOKEN"); // Token fetched at request time
  if (token && !config?.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API?.interceptors?.response?.use(
  (res) => res,
  (err) => {
    if (err?.response) {
      switch (err?.response?.status) {
        case 401:
          if (err?.config?.url === '/api/customers/add-feedback/v2' && isCdOrder()) {
            return err?.response;
          }
          // Check if we've already shown the error (first time only)
          const hasShown401 = hasSessionErrorBeenShown();
          if (!hasShown401 && !isOnPoliciesPage()) {
            // Toast for 401 error
            const state401 = Store.getState() as any | undefined;
            const navigationState401 = state401?.navigation as any;
            const isMobile401 = navigationState401?.isMobile;
            const message401 = "Session expired. Please sign in again.";
            if (isMobile401) {
              showErrorToast(message401);
            } else {
              showErrorToastDesktop(message401);
            }
            // Mark as shown
            markSessionErrorAsShown();
          }
          // Delete cookies on 401
          deleteCookie(USER_DETAILS);
          deleteCookie(USER_ID);
          deleteCookie(ACCESS_TOKEN);
             deleteCookie(SERVERKNOWNSALT);
          
          // Clear auth state to prevent auto-close issues
          Store.dispatch(removeauthInfo());
          
          // Dispatch Redux action to show Signupnew (only first time)
          if (!hasShown401) {
            // Use setTimeout to ensure state update happens after current execution
            setTimeout(() => {
              Store.dispatch(setSignupPopup(true));
            }, 0);
          }
          
          // Don't navigate - let the Signupnew show on current page
          // The global component will handle showing it everywhere
          
          return err?.response;
        case 403:
          if (err?.config?.url === '/api/customers/add-feedback/v2' && isCdOrder()) {
            return err?.response;
          }
          // Check if we've already shown the error (first time only)
          const hasShown403 = hasSessionErrorBeenShown();
          if (!hasShown403 && !isOnPoliciesPage()) {
            // Toast for 403 error
            const state = Store.getState() as any | undefined;
            const navigationState = state?.navigation as any;
            const isMobile = navigationState?.isMobile;
            const message = "Session expired. Please sign in again.";
            if (isMobile) {
              showErrorToast(message);
            } else {
              showErrorToastDesktop(message);
            }
            // Mark as shown
            markSessionErrorAsShown();
          }
          // Delete cookies on 403
          deleteCookie(USER_DETAILS);
          deleteCookie(USER_ID);
          deleteCookie(ACCESS_TOKEN);
          deleteCookie(SERVERKNOWNSALT);
          
          // Clear auth state to prevent auto-close issues
          Store.dispatch(removeauthInfo());
          
          // Dispatch Redux action to show Signupnew (only first time)
          if (!hasShown403) {
            // Use setTimeout to ensure state update happens after current execution
            setTimeout(() => {
              Store.dispatch(setSignupPopup(true));
            }, 0);
          }
          
          // Don't navigate - let the Signupnew show on current page
          // The global component will handle showing it everywhere
          
          return err?.response;
        case 409:
          return err?.response;
        case 400:
          return err?.response;
        case 500:
          return err?.response;
        default:
          break;
      }
    } else {
      console.error("API ERROR:", err);
    }
    throw err;
  }
);
const OFFERS_API = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT_REVAMP,
  timeout: 60000,
});

 


const attachInterceptors = (instance: any) => {
  /* ---------- Request interceptor ---------- */
  instance.interceptors.request.use((config: any) => {
    const token = getCookie(ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  /* ---------- Response interceptor ---------- */
  instance.interceptors.response.use(
    (res: any) => res,
    (err: any) => {
      if (err?.response) {
        const status = err.response.status;

        switch (status) {
          case 401:
          case 403: {
            if (err?.config?.url === '/api/customers/add-feedback/v2') {
              return err.response;
            }
            const hasShown = hasSessionErrorBeenShown();

            if (!hasShown && !isOnPoliciesPage()) {
              const state = Store.getState() as any;
              const isMobile = state?.navigation?.isMobile;
              const message = "Session expired. Please sign in again.";

              if (isMobile) {
                showErrorToast(message);
              } else {
                showErrorToastDesktop(message);
              }

              markSessionErrorAsShown();
            }

            // Clear cookies
            deleteCookie(USER_DETAILS);
            deleteCookie(USER_ID);
            deleteCookie(ACCESS_TOKEN);

            // Clear auth redux state
            Store.dispatch(removeauthInfo());

            // Show signup popup only once
            if (!hasShown) {
              setTimeout(() => {
                Store.dispatch(setSignupPopup(true));
              }, 0);
            }

            return err.response;
          }

          case 400:
          case 409:
          case 500:
            return err.response;

          default:
            break;
        }
      } else {
        console.error("API ERROR:", err);
      }

      throw err;
    }
  );
};

attachInterceptors(API);
attachInterceptors(OFFERS_API);
// attachInterceptors(NEW_API);
export { API, NEW_API, OFFERS_API };
export default API;

// ── Loyalty API Instance ──────────────────────────────
// Loyalty runs on a separate NestJS backend (REACT_APP_API_ENDPOINT_LOYALTY).
// - NO encryption (plain JSON)
// - x-brand-id header auto-injected
// - Same auth token handling as main API
const LOYALTY_API = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT_LOYALTY,
  timeout: 60000,
});

LOYALTY_API.interceptors.request.use((config) => {
  const token = getCookie("ACCESS_TOKEN");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Auto-inject brand ID from env (can be overridden per-request)
  if (!config.headers["x-brand-id"]) {
    config.headers["x-brand-id"] = process.env.REACT_APP_LOYALTY_BRAND_ID || "";
  }
  return config;
});

LOYALTY_API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response) {
      switch (err?.response?.status) {
        case 401:
        case 403: {
          const hasShown = hasSessionErrorBeenShown();
          if (!hasShown && !isOnPoliciesPage()) {
            const state = Store.getState() as any;
            const isMobile = state?.navigation?.isMobile;
            const message = "Session expired. Please sign in again.";
            isMobile ? showErrorToast(message) : showErrorToastDesktop(message);
            markSessionErrorAsShown();
          }
          deleteCookie(USER_DETAILS);
          deleteCookie(USER_ID);
          deleteCookie(ACCESS_TOKEN);
          Store.dispatch(removeauthInfo());
          if (!hasShown) {
            setTimeout(() => Store.dispatch(setSignupPopup(true)), 0);
          }
          return err.response;
        }
        case 409:
        case 400:
        case 500:
          return err.response;
        default:
          break;
      }
    } else {
      console.error("LOYALTY API ERROR:", err);
    }
    throw err;
  }
);

export { LOYALTY_API };
// Request Interceptor
// API.interceptors.request.use((config) => {
//   const token = getCookie("ACCESS_TOKEN"); // Token fetched at request time
//   if (token) {
//     config.headers.Authorization = `Bearer ${token}`;
//   }
//   return config;
// });

// API?.interceptors?.response?.use(
//   (res) => res,
//   (err) => {
//     if (err?.response) {
//       switch (err?.response?.status) {
//         case 401:
//           // Check if we've already shown the error (first time only)
//           const hasShown401 = hasSessionErrorBeenShown();
//           if (!hasShown401) {
//             // Toast for 401 error
//             const state401 = Store.getState() as any | undefined;
//             const navigationState401 = state401?.navigation as any;
//             const isMobile401 = navigationState401?.isMobile;
//             const message401 = "Session expired. Please sign in again.";
//             if (isMobile401) {
//               showErrorToast(message401);
//             } else {
//               showErrorToastDesktop(message401);
//             }
//             // Mark as shown
//             markSessionErrorAsShown();
//           }
//           // Delete cookies on 401
//           deleteCookie(USER_DETAILS);
//           deleteCookie(USER_ID);
//           deleteCookie(ACCESS_TOKEN);
          
//           // Clear auth state to prevent auto-close issues
//           Store.dispatch(removeauthInfo());
          
//           // Dispatch Redux action to show Signupnew (only first time)
//           if (!hasShown401) {
//             // Use setTimeout to ensure state update happens after current execution
//             setTimeout(() => {
//               Store.dispatch(setSignupPopup(true));
//             }, 0);
//           }
          
//           // Don't navigate - let the Signupnew show on current page
//           // The global component will handle showing it everywhere
          
//           return err?.response;
//         case 403:
//           // Check if we've already shown the error (first time only)
//           const hasShown403 = hasSessionErrorBeenShown();
//           if (!hasShown403) {
//             // Toast for 403 error
//             const state = Store.getState() as any | undefined;
//             const navigationState = state?.navigation as any;
//             const isMobile = navigationState?.isMobile;
//             const message = "Session expired. Please sign in again.";
//             if (isMobile) {
//               showErrorToast(message);
//             } else {
//               showErrorToastDesktop(message);
//             }
//             // Mark as shown
//             markSessionErrorAsShown();
//           }
//           // Delete cookies on 403
//           deleteCookie(USER_DETAILS);
//           deleteCookie(USER_ID);
//           deleteCookie(ACCESS_TOKEN);
          
//           // Clear auth state to prevent auto-close issues
//           Store.dispatch(removeauthInfo());
          
//           // Dispatch Redux action to show Signupnew (only first time)
//           if (!hasShown403) {
//             // Use setTimeout to ensure state update happens after current execution
//             setTimeout(() => {
//               Store.dispatch(setSignupPopup(true));
//             }, 0);
//           }
          
//           // Don't navigate - let the Signupnew show on current page
//           // The global component will handle showing it everywhere
          
//           return err?.response;
//         case 409:
//           return err?.response;
//         case 400:
//           return err?.response;
//         case 500:
//           return err?.response;
//         default:
//           break;
//       }
//     } else {
//       console.error("API ERROR:", err);
//     }
//     throw err;
//   }
// );




